import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchInvoicePeriodCheck,
  fetchInvoiceSyncStatus,
  fetchSubscriptionInvoices,
  previewInvoiceSync,
  runInvoiceSync,
} from "@/api/invoiceSync";
import type { SubscriptionInvoicesQuery } from "@/interfaces/invoiceSync";

/** Subscription invoices for Finance › Invoices. Keyed on the whole query. */
export function useSubscriptionInvoices(query: SubscriptionInvoicesQuery) {
  return useQuery({
    queryKey: ["subscriptionInvoices", query],
    queryFn: () => fetchSubscriptionInvoices(query),
    placeholderData: keepPreviousData,
  });
}

/** The last daily sync, for the status line. */
export function useInvoiceSyncStatus(enabled = true) {
  return useQuery({
    queryKey: ["invoiceSyncStatus"],
    queryFn: fetchInvoiceSyncStatus,
    enabled,
  });
}

/** What would block closing a month. Null `period` = don't ask (a closed month). */
export function useInvoicePeriodCheck(period: string | null) {
  return useQuery({
    queryKey: ["invoicePeriodCheck", period],
    queryFn: () => fetchInvoicePeriodCheck(period as string),
    enabled: period != null,
  });
}

/** Preview a sync (dry run). */
export function usePreviewInvoiceSync() {
  return useMutation({ mutationFn: previewInvoiceSync });
}

/** Run a sync, then refresh every list and check it can change. */
export function useRunInvoiceSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: runInvoiceSync,
    onSettled: () => {
      for (const key of [
        "subscriptionInvoices",
        "financeInvoices",
        "invoiceSyncStatus",
        "invoicePeriodCheck",
        "accountingPeriods",
        "journalEntries",
        "subscriptionRollForward",
        "subscriptionOrgDeferrals",
        "subscriptionSchedulesReconciliation",
      ]) {
        qc.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}
